import { Box } from '@mantine/core';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import { PlatformBanner } from '../components/app-shell/PlatformBanner';
import { HomeHeader } from '../components/home/HomeHeader';
import { MarketingPostscript } from '../components/home/MarketingPostscript';
import { StepExit } from '../components/home/StepExit';
import { StepFoundingOffer } from '../components/home/StepFoundingOffer';
import { StepHowItWorks } from '../components/home/StepHowItWorks';
import { StepInit } from '../components/home/StepInit';
import { StepInside } from '../components/home/StepInside';
import { StepNavigator } from '../components/home/StepNavigator';
import { StepWhatIs } from '../components/home/StepWhatIs';
import { StepWhosOn } from '../components/home/StepWhosOn';
import { StickyJoinCta } from '../components/home/StickyJoinCta';
import { SiteFooter } from '../components/ui/SiteFooter';
import { REFERRAL_SLUG_PATTERN, setReferralCookie } from '../lib/referral-cookie';

/**
 * Homepage. Scroll-narrative experience modelled on pi.dev, with a substantive
 * marketing postscript below.
 *
 * Composition:
 *   1. HomeHeader (sticky, translucent, brand stays present while scrolling)
 *   2. The narrative: the hero, the "What is wwaave?" definition, the "Who's on
 *      wwaave" image marquee, how the exchange works, a preview of the app
 *      itself, the WWAAVE Promise (currently hidden), the founding offer, and
 *      finally the closing artist/host CTA split
 *   3. StickyJoinCta, a persistent sign-up affordance so the reader can act
 *      before reaching the bottom
 *   4. MarketingPostscript, signposts to the deeper marketing pages
 *   5. SiteFooter (public), a proper footer so the page has a real ending
 *
 * The decorative mid-page world map was retired so the prime slot could go to
 * conversion (the founding offer + waitlist). The baked projection data is left
 * on disk but no longer imported.
 */
type HomeSearch = {
  /** Short-form share param `?r=<artistSlug>`, set on artist-shared links. */
  r?: string;
};

export const Route = createFileRoute('/')({
  // North Star §7 (T5). Capture `?r=<artistSlug>` at the URL boundary. Only
  // accept slug-shaped strings; junk silently drops. The home page effect
  // below stamps the cookie and strips the param so the trail URL stays clean.
  validateSearch: (s: Record<string, unknown>): HomeSearch => {
    const out: HomeSearch = {};
    const r = s.r;
    if (typeof r === 'string' && REFERRAL_SLUG_PATTERN.test(r.toLowerCase().trim())) {
      out.r = r.toLowerCase().trim();
    }
    return out;
  },
  component: HomePage,
});

function HomePage() {
  // Harvest `?r=<artistSlug>` into the share cookie + strip the param from
  // the URL so subsequent navigation + share-trail copies stay clean. The
  // cookie is read at sign-up time (see lib/referral-cookie.ts).
  const refShare = Route.useSearch({ select: (s) => s.r ?? null });
  const navigate = useNavigate();
  useEffect(() => {
    if (refShare === null) return;
    setReferralCookie(refShare);
    void navigate({
      to: '/',
      search: {},
      replace: true,
    });
  }, [refShare, navigate]);

  return (
    // overflow-x: clip (not hidden) contains the decorative bleed WITHOUT
    // creating a scroll container — `hidden` would, and that silently breaks
    // the sticky HomeHeader inside it (it'd scroll away). See feedback 2026-07-03.
    <Box style={{ backgroundColor: 'var(--wwaave-page-bg)', overflowX: 'clip' }}>
      <HomeHeader />
      <PlatformBanner />
      <StepInit />
      {/* The definition, directly below the hero (Richie, 2026-08-20). The hero
          names the thing; this says what the name means before the marquee
          below shows who it's for. */}
      <StepWhatIs />
      <StepWhosOn />
      <StepHowItWorks />
      {/* The Smart Rider used to sit here. It moved to the How It Works page
          (Richie, feedback DYFqwfvei8, 2026-08-09) — on the homepage it had no
          context around it and read as abstract. Its slot in the narrative went
          to the product preview, which is the thing a first-time reader was
          actually missing: seeing the app before deciding to join. */}
      <StepInside />
      {/* StepLogic (The WWAAVE Promise) is hidden for now at Richie's request
          (feedback KcsJsqnF, 2026-07-23). The component + its CMS wiring stay
          intact so it's a one-line restore; the StepNavigator dot and the
          scroll-hint targets were adjusted to skip it. */}
      <StepFoundingOffer />
      <StepExit />
      <StepNavigator />
      <StickyJoinCta />
      <MarketingPostscript />
      <SiteFooter variant="public" />
    </Box>
  );
}
